import { BreakpointObserver } from '@angular/cdk/layout';
import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Inject,
    OnInit,
    OnDestroy,
    AfterViewInit,
    ViewEncapsulation,
    PLATFORM_ID,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';

import { CategoryComponentClass } from 'lib/components/category/category-component.class';
import { CategoryService } from 'lib/services/category/category.service';
import { CmsService } from 'lib/services/cms.service';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { MetaService } from 'lib/services/meta.service';
import { RouterService } from 'lib/services/router.service';
import { WebstoreProductService } from 'lib/services/webstore-product/webstore-product.service';
import { WebpService } from 'lib/services/webp.service';
import { MarketoService } from 'lib/services/marketo.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { BootstrapMarketoService } from 'lib/services/bootstrap/bootstrap-marketo.service';
import { cmsMarketoIDsService } from 'lib/services/cmsMarketoIDs.service';
import { ConfiguratorService } from 'lib/services/configuration/configurator.service';
import { isConfigurationLive } from 'lib/services/configuration/configuration-status.util';

const scriptName = 'category.component';

@Component({
    selector: 'app-category',
    templateUrl: './category.component.html',
    styleUrls: ['./category.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class CategoryComponent extends CategoryComponentClass implements OnInit, OnDestroy, AfterViewInit {
    configuratorService: ConfiguratorService;
    configurations: any = new Map();
    private lastProductsMpns: string[] = [];
    private retrievingSubscription: Subscription;
    getWebpImg: (src: string) => string;
    bsModalRef?: BsModalRef;
    marketoId: string = '1080';
    constructor(
        @Inject(PLATFORM_ID) platformId: Object,
        @Inject('environment') e: any,
        b: BreakpointObserver,
        bsModalRef: BsModalRef,
        modalService: BsModalService,
        public bootstrapMarketoService: BootstrapMarketoService,
        public cmsMarketoIDsService: cmsMarketoIDsService,
        configuratorService: ConfiguratorService,
        ca: CategoryService,
        c: CmsService,
        changeDetectorRef: ChangeDetectorRef,
        d: MatDialog,
        m: MetaService,
        ma: MarketoService,
        pr: WebstoreProductService,
        r: RouterService,
        w: WebpService,
        a: ActivatedRoute,
        l: Location
    ) {
        super({
            dependencies: {
                platformId,
                bootstrapMarketoService: bootstrapMarketoService,
                breakpointObserver: b,
                bsModalRef: bsModalRef,
                modalService: modalService,
                configuratorService: configuratorService,
                categoryService: ca,
                changeDetectorRef: changeDetectorRef,
                cmsService: c,
                cmsMarketoIDsService: cmsMarketoIDsService,
                dialog: d,
                environment: e,
                marketoService: ma,
                metaService: m,
                productService: pr,
                routerService: r,
                activatedRoute: a,
                location: l,
            },
        });

        this.getWebpImg = (src: string) => w.getWebpImg(src);
    }

    async ngOnInit() {
        try {
            // Setup click listener for backdrop/dropdown interactions (browser only)
            if (this.isBrowser) {
                document.documentElement.addEventListener('click', this.hideIfBackdropClick.bind(this));
            }

            // Initialize CMS data and category service
            await this.init();

            // Load product configurations
            await this.populateConfigurations();

            // Listen for category service updates (handles initial load and some navigation)
            this.retrievingSubscription = this.categoryService.retrieving$.subscribe((retrieving) => {
                if (!retrieving) {
                    // Category finished loading/updating, trigger change detection for OnPush
                    this.changeDetectorRef.markForCheck();
                    // Category finished loading/updating, check for new configurations
                    setTimeout(() => this.populateConfigurations(), 100);
                }
            });
        } catch (err) {
            console.error(...new ExxComError(901234, scriptName, err).stamp());
        }
    }

    protected override async handleCategoryNotInPaths(category: string): Promise<void> {
        const isOffboarded = await this.categoryService.checkIfCategoryOffboarded(category);
        const route = isOffboarded ? '/410' : '/404';
        this.routerService.router.navigate([route], { skipLocationChange: true });
    }

    protected override async handleCategoryOffboarded(): Promise<void> {
        const component = this.routerService.url.component || '';
        const category = component.replace('category/', '');
        const isOffboarded = await this.categoryService.checkIfCategoryOffboarded(category);
        const route = isOffboarded ? '/410' : '/404';
        this.routerService.router.navigate([route], { skipLocationChange: true });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.retrievingSubscription) {
            this.retrievingSubscription.unsubscribe();
        }
    }

    ngAfterViewInit() {
        // Intentionally empty - lifecycle hook for future use
    }

    /**
     * @purpose makes a call to marketo id service and returns the corresponding id based on the manufacturer
     * @param product product information being passed to modal
     */
    async getProductIdOpenModal(product: any) {
        const res = await this.cmsMarketoIDsService.getFormIDs();
        const manufacturer = product.manufacturer.toLocaleLowerCase();
        if (manufacturer == 'exxact') {
            this.marketoId = `${res?.exx_solution_inquiry_id?.marketo_id}`;
        } else {
            this.marketoId = `${res?.exx_product_inquiry_id?.marketo_id}`;
        }
        this.bootstrapMarketoService.openBootstrapMarketoDialog(this.marketoId, product, { modelPartNumber: product.mpn });
    }

    async populateConfigurations() {
        if (!this.category?.products?.length) return;

        try {
            // Extract all MPNs from products
            const mpns = this.category.products.map((product) => product.mpn);

            // Check if products have changed since last fetch
            const mpnsChanged = JSON.stringify(mpns) !== JSON.stringify(this.lastProductsMpns);
            if (!mpnsChanged && this.configurations.size > 0) {
                return; // No need to refetch
            }

            // Single API call to fetch all configurations
            const configurationsMap = await this.configuratorService.getConfigurationsByMpns('exx', mpns, '', true, true);

            if (configurationsMap) {
                this.configurations = configurationsMap;
                this.lastProductsMpns = mpns; // Store current MPNs for comparison
                this.changeDetectorRef.markForCheck();
            }
        } catch (error) {
            console.error('Failed to fetch configurations:', error);
        }
    }

    // Whether the configuration for the given MPN is publicly visible (gates the inline configurator link).
    isConfigLive(mpn: string): boolean {
        return isConfigurationLive(this.configurations.get(mpn));
    }

    // Override base class methods to trigger configuration refresh when products change
    filter(group: any, facet: any) {
        super.filter(group, facet);
        // Defer to allow products to update first
        setTimeout(() => this.populateConfigurations(), 50);
    }

    clearAllFilters() {
        super.clearAllFilters();
        // Defer to allow products to update first
        setTimeout(() => this.populateConfigurations(), 50);
    }

    sort(i: any) {
        super.sort(i);
        // Defer to allow products to update first
        setTimeout(() => this.populateConfigurations(), 50);
    }

    onPageChanged() {
        // Handle pagination changes
        setTimeout(() => this.populateConfigurations(), 100);
    }

    // ==================== ST-1343: trackBy functions for *ngFor optimization - START ====================
    /**
     * TrackBy function for product lists
     * Prevents unnecessary DOM re-renders by tracking products by unique identifier
     */
    trackByProductId(index: number, product: any): any {
        return product?.id || product?.mpn || index;
    }

    /**
     * TrackBy function for facet groups
     */
    trackByFacetGroupIndex(index: number, group: any): any {
        return group?.index || index;
    }

    /**
     * TrackBy function for facets within groups
     */
    trackByFacetIndex(index: number, facet: any): any {
        return facet?.id || index;
    }

    /**
     * TrackBy function for sort options
     */
    trackBySortOptionIndex(index: number): number {
        return index;
    }

    /**
     * TrackBy function for active facets
     */
    trackByActiveFacetIndex(index: number, activeFacet: any): any {
        return `${activeFacet?.group?.index}-${activeFacet?.facet?.id}` || index;
    }
    // ==================== ST-1343: trackBy functions for *ngFor optimization - END ====================
}
